import { ArrowRight, Lock, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import EditorMockup from "@/components/site/EditorMockup";
import BlindajeHeroCard from "@/components/site/BlindajeHeroCard";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      {/* Subtle background ornaments */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
      >
        <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#E9E2D2]/60 blur-3xl" />
        <div className="absolute top-40 right-[-120px] h-[380px] w-[380px] rounded-full bg-[#D9DCD2]/50 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 sm:pt-20 lg:pt-24 pb-20 lg:pb-28">
        {/* Eyebrow */}
        <div
          data-testid="hero-eyebrow"
          className="flex items-center gap-3 animate-fade-up"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-rule bg-white/70 px-3 py-1.5 text-[11px] font-medium tracking-[0.18em] uppercase text-copper">
            <Sparkles size={12} className="text-copper" />
            Diseñado para México
          </span>
          <span className="hidden sm:inline text-xs text-ink-soft/80">
            UNAM · IPN · ITESM · UAM · UANL · BUAP · UdG
          </span>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: copy */}
          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "80ms" }}>
            <h1
              data-testid="hero-h1"
              className="font-serif text-[2.6rem] sm:text-5xl lg:text-[3.6rem] leading-[1.04] tracking-tight text-ink text-balance"
            >
              Gradúate{" "}
              <span className="italic font-medium text-forest">sin estrés</span>
              , sin errores de formato y con la{" "}
              <span className="italic font-medium text-copper">certeza</span> de
              que tu tesis será aprobada.
            </h1>

            <p
              data-testid="hero-h2"
              className="mt-7 text-[15px] sm:text-base leading-relaxed text-ink-soft max-w-xl"
            >
              El único copiloto asesor diseñado exclusivamente para la normativa
              universitaria de tu país. Mientras las IA genéricas inventan
              fuentes y rompen el formato, nosotros{" "}
              <span className="text-ink font-medium">
                blindamos tu investigación
              </span>{" "}
              con datos 100% reales, estructuramos tu tesis según el manual
              exacto de tu universidad y te entrenamos para defenderla con éxito
              ante el jurado.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
              <Link
                to="/signup"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-[15px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all shadow-[0_10px_30px_-12px_rgba(39,59,44,0.55)] hover:shadow-[0_18px_40px_-12px_rgba(39,59,44,0.65)] hover:-translate-y-[1px]"
              >
                Comenzar mi tesis gratis
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
              <p
                data-testid="hero-cta-subtext"
                className="text-xs sm:text-[13px] text-ink-soft/90 max-w-[240px] leading-snug"
              >
                Sin tarjetas de crédito.
                <br />
                Configura la plantilla de tu universidad en{" "}
                <span className="text-forest font-medium">60 segundos</span>.
              </p>
            </div>

            <div
              data-testid="hero-social-proof"
              className="mt-8 inline-flex items-center gap-3 rounded-lg border border-rule bg-paper-2 px-4 py-3"
            >
              <span className="grid place-items-center h-7 w-7 rounded-full bg-white border border-rule">
                <Lock size={13} className="text-forest" />
              </span>
              <p className="text-[13px] text-ink-soft leading-snug max-w-md">
                <span className="font-medium text-ink">
                  Metodología 100% ética.
                </span>{" "}
                Actuamos como tu tutor y validador científico privado,
                garantizando la propiedad intelectual de tu investigación.
              </p>
            </div>
          </div>

          {/* Right: mockup */}
          <div
            className="lg:col-span-7 animate-fade-up"
            style={{ animationDelay: "160ms" }}
          >
            <EditorMockup />
          </div>
        </div>

        {/* Blindaje Antiplagio Criptográfico — al fondo del hero */}
        <div className="animate-fade-up" style={{ animationDelay: "240ms" }}>
          <BlindajeHeroCard />
        </div>
      </div>
    </section>
  );
}
